/**
 * useZoomActual.ts — Zoom actual del mapa de Leaflet como estado de React (VECMUN-c).
 *
 * Hay que usarlo dentro de un `MapContainer` de react-leaflet. Devuelve el zoom y lo actualiza
 * en cada `zoomend`. Además lo lee al montarse: el encuadre inicial del mapa puede haber
 * cambiado el zoom antes de la suscripción y, entonces, ningún `zoomend` llega (VECMUN-b: el
 * estado seguía en 9 con el mapa en 11 y los nombres de los vecinos no salían hasta tocar el
 * zoom; en P2 pasa lo mismo, porque su `fitBounds` corre en un hijo anterior a las capas).
 *
 * Se apoya en `useSyncExternalStore`: el zoom se lee del mapa en cada render, y al suscribirse
 * React vuelve a leerlo, de modo que un cambio ocurrido entre el render y la suscripción
 * (el `fitBounds` del encuadre) se recoge sin escribir estado desde un efecto
 * (`react-hooks/set-state-in-effect`). Los avisos llegan por `zoomend`.
 *
 * Usa `useMapEvents` (no `useMap`) porque devuelve el mapa y suscribe en la misma llamada;
 * es la forma que ya simulan las pruebas de P3.
 *
 * Lo comparten el mapa de P3 (`MapaApoyoCotejo`, que sustituye con él su antiguo
 * `ZoomWatcher`) y el de P2 (`TerminoMunicipalCapa`, hijo de `MapViewer`).
 *
 * @version 1.1.0
 */

import { useCallback, useRef, useSyncExternalStore } from 'react';
import { useMapEvents } from 'react-leaflet';

export function useZoomActual(): number {
  const oyentes = useRef(new Set<() => void>());
  const map = useMapEvents({
    zoomend: () => oyentes.current.forEach((avisar) => avisar()),
  });
  const suscribir = useCallback((avisar: () => void) => {
    oyentes.current.add(avisar);
    return () => { oyentes.current.delete(avisar); };
  }, []);
  return useSyncExternalStore(suscribir, () => map.getZoom());
}

export default useZoomActual;
